import api from "@/lib/api";
import { logger } from "@/lib/logger";

/**
 * Browser push notification helpers — one-click enable, status check,
 * unsubscribe. Designed so non-tech users only see ONE "Allow" prompt
 * (the OS-level permission prompt) and then they're done.
 */

const isSupported = () =>
  typeof window !== "undefined"
  && "serviceWorker" in navigator
  && "PushManager" in window
  && "Notification" in window;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function registerServiceWorker() {
  if (!isSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    return null;
  }
}

export async function getCurrentSubscription() {
  if (!isSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch (e) {
    return null;
  }
}

export async function enablePushNotifications() {
  if (!isSupported()) {
    return { ok: false, reason: "Push notifications aren't supported here. On iPhone, open the installed app (Add to Home Screen) on iOS 16.4+." };
  }
  if (Notification.permission === "denied") {
    return { ok: false, reason: "Notifications are blocked. On iPhone: Settings → (this app) → Notifications → Allow. On desktop: click the lock icon → Notifications → Allow. Then reopen the app." };
  }

  // iOS PWA gotcha: Notification.requestPermission() MUST run synchronously
  // within the user gesture, BEFORE any `await`. Doing service-worker lookups
  // first consumes the user activation and the prompt is silently suppressed —
  // which is exactly why the installed app never subscribes. So request
  // permission FIRST, before anything else async.
  let perm = Notification.permission;
  if (perm !== "granted") {
    try {
      perm = await Notification.requestPermission();
    } catch (e) {
      // Older Safari only supports the callback form.
      perm = await new Promise((resolve) => {
        try { Notification.requestPermission(resolve); } catch (_) { resolve("denied"); }
      });
    }
  }
  if (perm !== "granted") {
    return { ok: false, reason: "Permission was not granted. Tap “Enable” again and choose Allow." };
  }

  // Now (after permission) set up the service worker + subscription.
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: "Couldn't start the notification service worker." };
  // Ensure the SW is active before subscribing (required on iOS).
  try { await navigator.serviceWorker.ready; } catch (e) { /* best effort */ }

  // Fetch VAPID public key
  const { data } = await api.get("/push/public-key");
  if (!data?.public_key) return { ok: false, reason: "Server is missing the VAPID key." };

  // Subscribe (reuse existing if present)
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  // Send to backend
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent,
  });
  return { ok: true, endpoint: json.endpoint };
}

export async function disablePushNotifications() {
  const sub = await getCurrentSubscription();
  if (!sub) return { ok: true };
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (e) {
    logger.warn("Push unsubscribe backend call failed:", e);
  }
  try {
    await sub.unsubscribe();
  } catch (e) {
    logger.warn("Browser unsubscribe failed:", e);
  }
  return { ok: true };
}

export async function sendTestPush() {
  return api.post("/push/test");
}

export function permissionState() {
  if (!isSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}
